import { Injectable } from '@angular/core';
import { Query, toBoolean } from '@datorama/akita';
import { AuthStore, AuthState } from './auth.store';

@Injectable({
  providedIn: 'root',
})
export class AuthQuery extends Query<AuthState> {
  public isLoggedIn$ = this.select((state) => toBoolean(state.access_token));

  public name$ = this.select((state) => state.name);
  public email$ = this.select((state) => state.email);
  public firststart$ = this.select((state) => state.firststart);

  public appid$ = this.select((state) => state.appid);
  public appdata$ = this.select((state) => state.appdata);
  public userid$ = this.select((state) => state.userid);

  public fid$ = this.select((state) => state.fid);
  public role$ = this.select((state) => state.role);
  public su$ = this.select((state) => state.su);
  public demoMode$ = this.select((state) => state.demo_mode);
  public permissions$ = this.select((state) => state.permissions);

  public profile_image$ = this.select((state) => state.profile_image);

  public access_token$ = this.select((state) => state.access_token);
  public version$ = this.select((state) => state.version);

  constructor(protected override store: AuthStore) {
    super(store);
  }

  get isLoggedIn() {
    return !!this.getValue().access_token;
  }

  get name() {
    return this.getValue().name;
  }

  get email() {
    return this.getValue().email;
  }

  get appid() {
    return this.getValue().appid;
  }

  get appdata() {
    return this.getValue().appdata;
  }

  get userid() {
    return this.getValue().userid;
  }

  get profile_image() {
    return this.getValue().profile_image;
  }

  get firststart_done() {
    return this.getValue().firststart_done;
  }

  set firststart_done(value) {
    this.getValue().firststart_done = value;
  }

  get access_token() {
    return this.getValue().access_token;
  }

  get version() {
    return this.getValue().version;
  }

  get role() {
    return this.getValue().role;
  }

  get roleName() {
    return this.getValue().role_name;
  }

  get su() {
    return Number(this.getValue().su ?? 0);
  }

  /** t_beuser.su = 1 means this login is a ParkraumPlus superuser (not demo-related). */
  get isSu(): boolean {
    return this.su === 1;
  }

  /** Global DEMO_MODE setting from DB settings JSON: 1 = demo restrictions active. */
  get demoModeActive(): boolean {
    return Number(this.getValue().demo_mode ?? 0) === 1;
  }

  /** Edit routes and bulk actions allowed when demo mode is off. */
  get editingAllowed(): boolean {
    return !this.demoModeActive;
  }

  get permissions(): { [module: string]: boolean } {
    return this.getValue().permissions || {};
  }

  /** Superuser: role 1 (ParkraumPlus group) with su flag set. */
  get isSuperuser(): boolean {
    return Number(this.getValue().role) === 1 && this.isSu;
  }

  can(module: string): boolean {
    if (this.isSuperuser) {
      return true;
    }
    return !!this.permissions?.[module];
  }

  // ---------------------------------------------------------------
  // Role helpers (beusergroup_id):
  //   1 = Superuser, 2 = Admin, 3 = Strafamt, 4 = Organ,
  //   5 = Finanzen, 6 = Verwaltung
  // ---------------------------------------------------------------
  get isAdmin(): boolean {
    const r = Number(this.getValue().role);
    return r === 1 || r === 2;
  }

  get isEditor(): boolean {
    return Number(this.getValue().role) === 6;
  }

  get isErfasser(): boolean {
    return Number(this.getValue().role) === 4;
  }

  get dienstnr() {
    return this.getValue().dienstnr;
  }

  get fid() {
    return this.getValue().fid;
  }

  get country() {
    return this.getValue().country;
  }
}
